'use client'

/**
 * LG-26 — live sample signals for the marketing showcase surfaces.
 *
 * Three marketing surfaces (hero signal feed, globe "Today's signals"
 * ticker, the "What you see on day one" MockDashboard) render REAL
 * recent signals from the public, unauthenticated endpoint
 * GET /api/v1/public/sample-signals instead of fabricated company events.
 *
 * Design constraints (the marketing hero must NEVER go blank):
 *  - client-side fetch only (the surfaces are 'use client' components),
 *  - ~15 min sessionStorage cache so the 3 surfaces + navigations don't
 *    hammer the endpoint,
 *  - a single in-flight promise shared across the 3 surfaces,
 *  - graceful fallback: any failure / empty payload resolves to `null`
 *    and each surface keeps its clearly-marked static fallback content.
 *
 * The endpoint is being built in parallel on the backend; we defensively
 * accept BOTH a bare array and a `{ signals: [...] }` wrapper, and
 * normalize severities outside the canonical vocabulary.
 */

import { useEffect, useState } from 'react'

export type SampleSignalSeverity = 'critical' | 'notable' | 'info'

export type SampleSignal = {
  company_name: string
  event_type: string
  severity: SampleSignalSeverity
  summary: string
  occurred_at: string | null
}

// Mirrors src/lib/api.ts — keep in sync. We deliberately use plain fetch
// (not the axios instance): this endpoint is public, must not attach a JWT,
// and must not trigger the 401-redirect interceptor on a marketing page.
const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.pulsesignal.co'
export const SAMPLE_SIGNALS_ENDPOINT = `${API_URL}/api/v1/public/sample-signals`

export const SAMPLE_SIGNALS_CACHE_KEY = 'ps:sample-signals:v1'
export const SAMPLE_SIGNALS_CACHE_TTL_MS = 15 * 60 * 1000 // ~15 minutes

// ─── Parsing / normalization ─────────────────────────────────────────────────

function normalizeSeverity(raw: unknown): SampleSignalSeverity {
  const s = typeof raw === 'string' ? raw.toLowerCase().trim() : ''
  if (s === 'critical' || s === 'high' || s === 'urgent') return 'critical'
  if (s === 'notable' || s === 'medium' || s === 'moderate' || s === 'warning') return 'notable'
  return 'info'
}

/**
 * Accepts either a bare array or `{ signals: [...] }` (exact backend wrapper
 * unconfirmed while it is built in parallel). Invalid entries are dropped;
 * a non-array payload yields [].
 */
export function parseSampleSignals(payload: unknown): SampleSignal[] {
  let items: unknown
  if (Array.isArray(payload)) {
    items = payload
  } else if (payload && typeof payload === 'object' && 'signals' in payload) {
    items = (payload as { signals: unknown }).signals
  }
  if (!Array.isArray(items)) return []

  const out: SampleSignal[] = []
  for (const item of items) {
    if (!item || typeof item !== 'object') continue
    const rec = item as Record<string, unknown>
    const company = typeof rec.company_name === 'string' ? rec.company_name.trim() : ''
    const summary = typeof rec.summary === 'string' ? rec.summary.trim() : ''
    if (!company || !summary) continue
    out.push({
      company_name: company,
      event_type: typeof rec.event_type === 'string' ? rec.event_type : '',
      severity: normalizeSeverity(rec.severity),
      summary,
      occurred_at: typeof rec.occurred_at === 'string' ? rec.occurred_at : null,
    })
  }
  return out
}

// ─── sessionStorage cache (best-effort; Safari private mode throws) ─────────

function readCache(): SampleSignal[] | null {
  try {
    const raw = window.sessionStorage.getItem(SAMPLE_SIGNALS_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { at?: unknown; signals?: unknown }
    if (typeof parsed?.at !== 'number') return null
    if (Date.now() - parsed.at > SAMPLE_SIGNALS_CACHE_TTL_MS) return null
    // Re-run the defensive parser on the cached payload so a stale or
    // hand-edited cache can never inject malformed rows into the UI.
    const signals = parseSampleSignals(parsed.signals)
    return signals.length > 0 ? signals : null
  } catch {
    return null
  }
}

function writeCache(signals: SampleSignal[]): void {
  try {
    window.sessionStorage.setItem(
      SAMPLE_SIGNALS_CACHE_KEY,
      JSON.stringify({ at: Date.now(), signals }),
    )
  } catch {
    // best-effort: quota / private-mode failures just skip the cache
  }
}

// ─── Fetch (single-flight) ───────────────────────────────────────────────────

let inflight: Promise<SampleSignal[] | null> | null = null

/**
 * Resolves to the live sample signals, or `null` on ANY failure / empty
 * payload (callers then keep their static fallback). Never throws.
 */
export function fetchSampleSignals(): Promise<SampleSignal[] | null> {
  const cached = readCache()
  if (cached) return Promise.resolve(cached)

  if (!inflight) {
    inflight = (async (): Promise<SampleSignal[] | null> => {
      try {
        const res = await fetch(SAMPLE_SIGNALS_ENDPOINT, {
          headers: { Accept: 'application/json' },
        })
        if (!res.ok) return null
        const signals = parseSampleSignals(await res.json())
        if (signals.length === 0) return null
        writeCache(signals)
        return signals
      } catch {
        return null
      }
    })()
    // Always clear so a later mount can retry after a failure; successes
    // are served from the sessionStorage cache for ~15 min.
    inflight.finally(() => {
      inflight = null
    })
  }
  return inflight
}

/**
 * Returns the live signals once loaded, `null` before that and on failure.
 * Surfaces render their static fallback while this is `null`, so the hero
 * never paints blank.
 */
export function useSampleSignals(): SampleSignal[] | null {
  const [signals, setSignals] = useState<SampleSignal[] | null>(null)
  useEffect(() => {
    let cancelled = false
    fetchSampleSignals().then((s) => {
      if (!cancelled && s && s.length > 0) setSignals(s)
    })
    return () => {
      cancelled = true
    }
  }, [])
  return signals
}

// ─── Display helpers shared by the three surfaces ────────────────────────────

/** "PRICING.PLAN_CHANGE" → "Pricing"; empty/garbage → "Signal". */
export function signalModuleLabel(eventType: string): string {
  const head = (eventType || '').split(/[._:\s/-]/)[0]
  if (!head) return 'Signal'
  const lower = head.toLowerCase()
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

/** Hero "when" column: Today / Yesterday / "Jun 8" / Recent (unparseable). */
export function signalDayLabel(occurredAt: string | null): string {
  if (!occurredAt) return 'Recent'
  const d = new Date(occurredAt)
  if (Number.isNaN(d.getTime())) return 'Recent'
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const t = d.getTime()
  if (t >= startOfToday) return 'Today'
  if (t >= startOfToday - 24 * 60 * 60 * 1000) return 'Yesterday'
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/** Compact relative-time tag: now / 14m / 3h / 2d; unparseable → "new". */
export function signalAgoLabel(occurredAt: string | null): string {
  if (!occurredAt) return 'new'
  const d = new Date(occurredAt)
  if (Number.isNaN(d.getTime())) return 'new'
  const diffMs = Date.now() - d.getTime()
  if (diffMs < 60 * 1000) return 'now'
  const mins = Math.floor(diffMs / (60 * 1000))
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}
